import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PageContainer, PageContent } from '@/components/ui/page-layout';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import CTUCompaniesHero from '../components/ctu-companies/CTUCompaniesHero';
import CTUCompaniesByYearChart from '../components/ctu-companies/CTUCompaniesByYearChart';
import CTUCompaniesByFacultyChart from '../components/ctu-companies/CTUCompaniesByFacultyChart';
import CTUCompaniesTable from '../components/ctu-companies/CTUCompaniesTable';
import ChartSkeleton from '../components/dashboard/ChartSkeleton';
import { getCtuCompanies } from '../services/apiService';
import type { CtuCompany, CtuCompaniesStats } from '../types/api';

export default function SpinoffsPage() {
  const { t } = useTranslation('ctuCompanies');
  const [companies, setCompanies] = useState<CtuCompany[]>([]);
  const [stats, setStats] = useState<CtuCompaniesStats | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [errored, setErrored] = useState<boolean>(false);
  const [errorDetail, setErrorDetail] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setErrored(false);
    setErrorDetail(null);
    getCtuCompanies()
      .then((data) => {
        if (cancelled) return;
        setCompanies(data.companies);
        setStats(data.stats);
      })
      .catch((err: any) => {
        if (cancelled) return;
        setErrored(true);
        setErrorDetail(err?.message || null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <PageContainer>
      <PageContent>
        {loading && (
          <div aria-busy="true" aria-label={t('loading')}>
            <Card className="mb-6 border-ctu-border shadow-none">
              <CardContent className="p-8">
                <Skeleton className="h-8 w-64 mb-3" />
                <Skeleton className="h-4 w-96 max-w-full" />
                <div className="my-6 h-px bg-border" />
                <div className="grid grid-cols-3 gap-x-8 gap-y-4">
                  {Array.from({ length: 3 }).map((_, i) => (
                    <div key={i} className="flex flex-col gap-2">
                      <Skeleton className="h-3 w-20" />
                      <Skeleton className="h-7 w-16" />
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            <div className="mb-6">
              <ChartSkeleton height={320} />
            </div>
            <div className="mb-6">
              <ChartSkeleton height={320} />
            </div>

            <Card className="border-ctu-border shadow-none">
              <CardContent className="px-6 pt-5 pb-4">
                <Skeleton className="h-3 w-24 mb-4" />
                <div className="flex flex-col gap-2">
                  {Array.from({ length: 8 }).map((_, i) => (
                    <Skeleton key={i} className="h-9 w-full" />
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {errored && !loading && (
          <Alert variant="destructive" className="mb-6">
            <AlertDescription>{errorDetail || t('error')}</AlertDescription>
          </Alert>
        )}

        {!loading && !errored && stats && (
          <>
            <CTUCompaniesHero
              spinoffsCount={stats.spinoffs}
              startupsCount={stats.startups}
              foundersCount={stats.founders}
            />
            <CTUCompaniesByYearChart companies={companies} />
            <CTUCompaniesByFacultyChart companies={companies} />
            <CTUCompaniesTable companies={companies} />
          </>
        )}
      </PageContent>
    </PageContainer>
  );
}
